/**
 * Volume data hooks (#874). Request budget:
 *   - App-wide (#1809): ONE spotMetaAndAssetCtxs request (24 h volume of every outcome), refreshed every 60 s while the
 *     tab is visible, from site entry (components/GlobalStreams.tsx), so Markets opens ready. No per-row requests.
 *   - Market page: that same cached query + two candleSnapshot requests for the outcome's side-0 coin, both
 *     through the app-wide candle throttle (throttle.ts), never retried: "1h" from the previous hour (<= 2 candles,
 *     every 60 s) and "1d" from startTime 0 (lifetime, every 5 min).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { HlHttpError } from '../hl/info';
import { hlQueryKey, useHlEnv, useInfoClient, useOnTabWake } from '../hl/react';
import { backgroundPacer } from '../hl/weightPacer';
import type { SpotMetaAndAssetCtxsResponse } from '../hl/types';
import { candleThrottle } from './throttle';
import {
  DAY_INTERVAL,
  HOUR_INTERVAL,
  LIFETIME_START_MS,
  dayVolumes,
  hourWindow,
  marketVolume,
  volumeCoin,
  type DayVolume,
  type MarketVolume,
} from './volume';

export const VOLUME_REFRESH_MS = 60_000;
/** Weight of one spotMetaAndAssetCtxs read. */
const SPOT_META_AND_CTXS_WEIGHT = 20;

/**
 * ~0.35 MB (mainnet) to ~1 MB (testnet) answer: keep only the per-outcome map. Module-level so TanStack Query runs it
 * once per answer, not on every render.
 */
const selectDayVolumes = (data: SpotMetaAndAssetCtxsResponse): Map<number, DayVolume> =>
  dayVolumes(Array.isArray(data) ? data[1] : undefined);

function useDayVolumesQuery() {
  const env = useHlEnv();
  const info = useInfoClient();
  return {
    queryKey: hlQueryKey(env, 'spotMetaAndAssetCtxs'),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      await backgroundPacer(info).take(SPOT_META_AND_CTXS_WEIGHT, { signal });
      return info.spotMetaAndAssetCtxs({ signal });
    },
    staleTime: VOLUME_REFRESH_MS / 2,
  };
}

/**
 * Keeps the 24 h volume fresh: GlobalStreams only. This must stay the one observer with an interval: TanStack gives each
 * observer its own timer and does not check freshness when it fires, so a second one is a second request a minute.
 * TanStack skips the interval while the tab is hidden. A tab back from sleep reads once instead of waiting for the next tick.
 */
export function useHoldDayVolumes(): void {
  const query = useDayVolumesQuery();
  const qc = useQueryClient();
  useQuery({ ...query, refetchInterval: VOLUME_REFRESH_MS });
  useOnTabWake(() => void qc.refetchQueries({ queryKey: query.queryKey, exact: true }, { cancelRefetch: false }));
}

/**
 * 24 h volume of every outcome (by outcome id); undefined until the first answer. Pass enabled=false to skip.
 * Reads what useHoldDayVolumes keeps fresh: mounting a page sends nothing once there is data.
 */
export function useDayVolumes(enabled = true): { volumes: Map<number, DayVolume> | undefined; error: unknown } {
  const q = useQuery({ ...useDayVolumesQuery(), enabled, refetchOnMount: false, select: selectDayVolumes });
  return { volumes: q.data, error: q.error };
}

export const LIFETIME_REFRESH_MS = 5 * 60_000;

export interface MarketVolumeResult {
  volume: MarketVolume;
  /** Candles still loading (24 h may already be known). */
  loading: boolean;
  /** Every source failed. */
  error: boolean;
}

/** HTTP 500 `null`: a coin Hyperliquid does not know (e.g. it has just settled) = no candle data. */
function unknownCoin(e: unknown): boolean {
  return e instanceof HlHttpError && e.status === 500 && e.body.trim() === 'null';
}

/** 24 h, last-hour and lifetime volume of one outcome (market page). */
export function useMarketVolume(outcome: number | null): MarketVolumeResult {
  const env = useHlEnv();
  const info = useInfoClient();
  const day = useDayVolumes(outcome !== null);
  const coin = outcome !== null ? volumeCoin(outcome) : null;
  const fetchCandles = (interval: string, startTime: number, endTime: number, signal: AbortSignal) =>
    candleThrottle.run(async () => {
      try {
        return await info.candleSnapshot(coin!, interval, startTime, endTime, { signal, maxRetries: 0 });
      } catch (e) {
        if (unknownCoin(e)) return null;
        throw e;
      }
    }, signal);
  const hour = useQuery({
    queryKey: hlQueryKey(env, 'candleSnapshot', coin, HOUR_INTERVAL),
    enabled: coin !== null,
    staleTime: VOLUME_REFRESH_MS / 2,
    refetchInterval: VOLUME_REFRESH_MS,
    retry: false,
    queryFn: async ({ signal }) => {
      const nowMs = Date.now();
      const { startTime, endTime } = hourWindow(nowMs);
      return { candles: await fetchCandles(HOUR_INTERVAL, startTime, endTime, signal), nowMs };
    },
  });
  const life = useQuery({
    queryKey: hlQueryKey(env, 'candleSnapshot', coin, DAY_INTERVAL),
    enabled: coin !== null,
    staleTime: LIFETIME_REFRESH_MS / 2,
    refetchInterval: LIFETIME_REFRESH_MS,
    retry: false,
    queryFn: ({ signal }) => fetchCandles(DAY_INTERVAL, LIFETIME_START_MS, Date.now(), signal),
  });
  const dayVol = outcome !== null ? day.volumes?.get(outcome) : undefined;
  const h = hour.data;
  const l = life.data;
  const volume = useMemo(
    () => marketVolume(dayVol, h ? h.candles : undefined, life.isSuccess ? l : undefined, h ? h.nowMs : Date.now()),
    [dayVol, h, l, life.isSuccess],
  );
  return {
    volume,
    loading: coin !== null && (hour.isPending || life.isPending),
    error: hour.isError && life.isError && day.error != null,
  };
}
